import { create } from 'zustand';

import type { GapSortDirection } from './copy-token-flags';

/**
 * Launcher V2 B2 (plan `2026-09-01-001`, Batch 11) — whether the Qualify
 * step's `MembersPanel` slot is expanded.
 *
 * Collapsed by default (`initialState.qualifyMembersOpen = false`): the
 * table isn't useful until a rung's "Preview" has scoped it to real members,
 * so Qualify opens with it out of the way.
 *
 * Why a store at all (and not `CollapsibleCard`'s own `useState`-owned
 * `open`, which is the default for every other `CollapsibleCard` in this
 * tree): a rung row's "Preview" click, which must expand this panel, lives
 * in `WaterfallCard` — a SIBLING of the `CollapsibleCard` that wraps
 * `membersPanel` in `QualifyStep`, not a descendant of it (same reason the
 * click already writes to the module-scope `rung-scope-store` rather than
 * component state). Threading an `onOpen` callback down through
 * `QualifyStep`'s props into `WaterfallCard` would work too, but would only
 * serve this one flip; a store matches root CLAUDE.md's "Zustand for
 * Module-Scope Client State" convention and the identical shape
 * `sequence-preview-store.ts`/`rung-scope-store.ts` already use for the same
 * sibling-communication problem.
 *
 * `PlayFlowShell` resets this store to {@link initialState} on mount AND
 * unmount, keyed on `[playId]`, alongside its other play-flow-scoped stores
 * — a previous Play's expanded/collapsed choice must not leak into the next
 * Play routed through this same shell instance.
 *
 * A3 (plan `2026-09-01-003`) — `gapSortDirection` (the "Missing fields"
 * column's sort direction) joined this store for the identical structural
 * reason: the column header that flips it lives in `MembersPanel`, which
 * unmounts/remounts on every tab switch (see this store's own header, and
 * `MembersPanel.tsx`'s own module doc) — component-local `useState` would
 * silently reset the rep's chosen direction on the very next switch. It is
 * reset by `clearMembersPanelCollapse()` alongside the collapse bit, so it
 * stays play-scoped without any change to `PlayFlowShell`'s existing reset
 * effect.
 */

interface MembersPanelCollapseState {
  qualifyMembersOpen: boolean;
  gapSortDirection: GapSortDirection;
}

/** Exported per the store convention — reset idiom is `useMembersPanelCollapseStore.setState(initialState, true)`. */
export const initialState: MembersPanelCollapseState = {
  qualifyMembersOpen: false,
  gapSortDirection: 'missing-first',
};

export const useMembersPanelCollapseStore = create<MembersPanelCollapseState>(() => ({ ...initialState }));

export function setQualifyMembersOpen(open: boolean): void {
  useMembersPanelCollapseStore.setState({ qualifyMembersOpen: open });
}

/** Toggles/sets the "Missing fields" column's sort direction (A3). */
export function setGapSortDirection(direction: GapSortDirection): void {
  useMembersPanelCollapseStore.setState({ gapSortDirection: direction });
}

/** Resets to the initial (collapsed, missing-first) state — the Play-scoped reset `PlayFlowShell` performs. */
export function clearMembersPanelCollapse(): void {
  useMembersPanelCollapseStore.setState({ ...initialState });
}

export function selectQualifyMembersOpen(state: MembersPanelCollapseState): boolean {
  return state.qualifyMembersOpen;
}

export function selectGapSortDirection(state: MembersPanelCollapseState): GapSortDirection {
  return state.gapSortDirection;
}
